import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 摄影技巧 - 横屏「取景分析」风格
 *
 * 核心亮点：
 * - 取景框网格覆盖（三分/黄金/对角线）
 * - 相机参数仪表盘
 * - 直方图动画
 * - 拍摄要点卡片
 *
 * 尺寸：1920x1080 横屏
 */

interface CameraParam {
  label: string;
  value: string;
  icon: string;
}

interface PhotoTip {
  title: string;
  detail: string;
}

interface PhotographyTemplateProps {
  topic: string;
  subtitle: string;
  technique: string;
  cameraParams: CameraParam[];
  histogram: number[];
  tips: PhotoTip[];
  composition: 'thirds' | 'golden' | 'diagonal' | 'center';
  keyPoint: string;
}

const DEFAULT_PROPS: PhotographyTemplateProps = {
  topic: '黄金时刻人像摄影',
  subtitle: 'Golden Hour Portrait Photography',
  technique: '逆光人像 + 大光圈虚化',
  cameraParams: [
    { label: '光圈', value: 'f/1.8', icon: '🔘' },
    { label: '快门', value: '1/500s', icon: '⚡' },
    { label: 'ISO', value: '200', icon: '🔆' },
    { label: '焦距', value: '85mm', icon: '🔭' },
    { label: '白平衡', value: '5600K', icon: '🌡️' },
    { label: '测光', value: '点测光', icon: '🎯' },
  ],
  histogram: [5, 8, 15, 25, 40, 60, 80, 95, 85, 70, 55, 40, 30, 20, 12, 8, 5, 3, 8, 15, 25, 35, 45, 30, 15],
  tips: [
    { title: '时间选择', detail: '日出后/日落前 1 小时，光线柔和金黄' },
    { title: '对焦技巧', detail: '使用单点对焦锁定眼睛，保持眼神锐利' },
    { title: '逆光处理', detail: '让阳光从侧后方打来，创造发丝光和轮廓光' },
    { title: '后期方向', detail: '降低高光、提升暗部，增加橙色色温' },
  ],
  composition: 'thirds',
  keyPoint: '好的人像照片 = 正确的光线 × 精准的对焦 × 自然的表情',
};

export const PhotographyTemplate: React.FC<Partial<PhotographyTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });

  const vfW = 780;
  const vfH = 520;
  const vfDraw = interpolate(frame, [20, 60], [0, 1], { extrapolateRight: 'clamp' });

  const paramStart = 50;
  const paramInterval = 15;
  const histStart = paramStart + p.cameraParams.length * paramInterval + 20;
  const histDraw = interpolate(frame, [histStart, histStart + 40], [0, 1], { extrapolateRight: 'clamp' });
  const tipStart = histStart + 60;
  const tipInterval = 40;
  const kpStart = tipStart + p.tips.length * tipInterval + 20;
  const kpOpacity = interpolate(frame, [kpStart, kpStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  const shutterFlash = frame > 35 && frame < 40 ? interpolate(frame, [35, 37, 40], [0, 0.5, 0], { extrapolateRight: 'clamp' }) : 0;

  const rightW = 910;

  return (
    <AbsoluteFill
      style={{
        background: '#111111',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{ position: 'absolute', top: 28, left: 50, right: 50, display: 'flex', alignItems: 'center', gap: 16, opacity: headerProgress }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <div style={{ padding: '6px 18px', background: '#f9731618', border: '1px solid #f9731640', borderRadius: 10, fontSize: 20, color: '#fb923c', fontWeight: 700, whiteSpace: 'nowrap' }}>
            📷 PHOTOGRAPHY
          </div>
          <span style={{ fontSize: 20, color: 'rgba(255,255,255,0.25)', whiteSpace: 'nowrap' }}>{p.technique}</span>
        </div>
        <h1 style={{ fontSize: 40, fontWeight: 900, margin: 0, whiteSpace: 'nowrap' }}>{p.topic}</h1>
        <span style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', whiteSpace: 'nowrap' }}>{p.subtitle}</span>
      </div>

      {/* ========== VIEWFINDER ========== */}
      <div
        style={{
          position: 'absolute',
          left: 50,
          top: 200,
          width: vfW,
          height: vfH,
          border: '2px solid rgba(255,255,255,0.15)',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        {/* Golden hour gradient placeholder */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #1a0a00 0%, #3d1a00 30%, #f97316 70%, #fbbf24 100%)', opacity: 0.15 }} />

        {/* Shutter flash */}
        <div style={{ position: 'absolute', inset: 0, background: 'white', opacity: shutterFlash, pointerEvents: 'none' }} />

        {/* Composition grid */}
        <svg width={vfW} height={vfH} viewBox={`0 0 ${vfW} ${vfH}`} style={{ position: 'absolute', inset: 0 }}>
          {p.composition === 'thirds' && (
            <>
              {[1 / 3, 2 / 3].map((t) => (
                <React.Fragment key={t}>
                  <line x1={t * vfW} y1={0} x2={t * vfW} y2={vfH * vfDraw} stroke="#fb923c" strokeWidth="1" opacity="0.4" />
                  <line x1={0} y1={t * vfH} x2={vfW * vfDraw} y2={t * vfH} stroke="#fb923c" strokeWidth="1" opacity="0.4" />
                </React.Fragment>
              ))}
              {[1 / 3, 2 / 3].map((tx) =>
                [1 / 3, 2 / 3].map((ty) => (
                  <circle key={`${tx}-${ty}`} cx={tx * vfW} cy={ty * vfH} r={5 * vfDraw} fill="#fb923c" opacity="0.5" />
                ))
              )}
            </>
          )}
          {p.composition === 'golden' && (
            <>
              {[0.382, 0.618].map((t) => (
                <React.Fragment key={t}>
                  <line x1={t * vfW} y1={0} x2={t * vfW} y2={vfH * vfDraw} stroke="#fbbf24" strokeWidth="1" opacity="0.4" />
                  <line x1={0} y1={t * vfH} x2={vfW * vfDraw} y2={t * vfH} stroke="#fbbf24" strokeWidth="1" opacity="0.4" />
                </React.Fragment>
              ))}
            </>
          )}
          {p.composition === 'diagonal' && (
            <>
              <line x1={0} y1={0} x2={vfW * vfDraw} y2={vfH * vfDraw} stroke="#fb923c" strokeWidth="1" opacity="0.3" />
              <line x1={vfW} y1={0} x2={vfW * (1 - vfDraw)} y2={vfH * vfDraw} stroke="#fb923c" strokeWidth="1" opacity="0.3" />
            </>
          )}

          {/* Focus box */}
          <rect x={vfW * 0.33 - 35} y={vfH * 0.33 - 30} width={70} height={60} fill="none" stroke="#fb923c" strokeWidth="2" opacity={vfDraw > 0.8 ? 0.8 : 0} />
          {vfDraw > 0.8 && (
            <text x={vfW * 0.33} y={vfH * 0.33 + 40} fill="#fb923c" fontSize="13" textAnchor="middle" opacity="0.6">AF</text>
          )}
        </svg>

        {/* Composition label */}
        <div style={{ position: 'absolute', bottom: 12, left: 12, padding: '4px 14px', background: 'rgba(0,0,0,0.5)', borderRadius: 6, fontSize: 22, color: '#fb923c', opacity: vfDraw, fontWeight: 600 }}>
          🎯 {p.composition === 'thirds' ? '三分法构图' : p.composition === 'golden' ? '黄金分割' : p.composition === 'diagonal' ? '对角线构图' : '中心构图'}
        </div>

        {/* Corner brackets */}
        {[[0, 0], [vfW - 30, 0], [0, vfH - 30], [vfW - 30, vfH - 30]].map(([x, y], i) => (
          <svg key={i} style={{ position: 'absolute', left: x, top: y }} width="30" height="30">
            <path d={i < 2 ? (i === 0 ? 'M0,20 L0,0 L20,0' : 'M10,0 L30,0 L30,20') : (i === 2 ? 'M0,10 L0,30 L20,30' : 'M10,30 L30,30 L30,10')}
              fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2" opacity={vfDraw} />
          </svg>
        ))}
      </div>

      {/* ========== RIGHT PANEL ========== */}
      <div style={{ position: 'absolute', top: 190, left: 940, width: rightW }}>
        {/* Camera params */}
        <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', marginBottom: 14, letterSpacing: '0.1em', fontWeight: 600 }}>相机参数</div>
        <div style={{ display: 'flex', gap: 10 }}>
          {p.cameraParams.map((param, i) => {
            const start = paramStart + i * paramInterval;
            const opacity = interpolate(frame, [start, start + 12], [0, 1], { extrapolateRight: 'clamp' });
            return (
              <div
                key={i}
                style={{
                  flex: 1,
                  padding: '12px 8px',
                  background: 'rgba(255,255,255,0.03)',
                  borderRadius: 12,
                  border: '1px solid rgba(255,255,255,0.06)',
                  textAlign: 'center',
                  opacity,
                }}
              >
                <div style={{ fontSize: 28 }}>{param.icon}</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: '#fb923c', marginTop: 4 }}>{param.value}</div>
                <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>{param.label}</div>
              </div>
            );
          })}
        </div>

        {/* Histogram */}
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', marginBottom: 10, fontWeight: 600 }}>直方图</div>
          <svg width={rightW} height="90" viewBox={`0 0 ${rightW} 90`}>
            <defs>
              <linearGradient id="histGrad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#1a1a1a" />
                <stop offset="50%" stopColor="#888" />
                <stop offset="100%" stopColor="#fbbf24" />
              </linearGradient>
            </defs>
            {p.histogram.map((v, i) => {
              const barH = (v / 100) * 80 * histDraw;
              return (
                <rect
                  key={i}
                  x={i * (rightW / p.histogram.length)}
                  y={85 - barH}
                  width={rightW / p.histogram.length - 1}
                  height={barH}
                  fill="url(#histGrad)"
                  opacity="0.6"
                  rx="1"
                />
              );
            })}
          </svg>
        </div>

        {/* Tips */}
        <div style={{ marginTop: 22 }}>
          <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', marginBottom: 12, letterSpacing: '0.1em', fontWeight: 600 }}>拍摄要点</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {p.tips.map((tip, i) => {
              const start = tipStart + i * tipInterval;
              const progress = spring({ frame: frame - start, fps, from: 0, to: 1, config: { damping: 14, stiffness: 120, mass: 0.5 } });
              return (
                <div
                  key={i}
                  style={{
                    padding: '16px 20px',
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: 12,
                    border: '1px solid rgba(255,255,255,0.05)',
                    opacity: progress,
                    transform: `translateX(${(1 - progress) * 15}px)`,
                  }}
                >
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#fb923c', marginBottom: 4 }}>📌 {tip.title}</div>
                  <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>{tip.detail}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Key point */}
      <div style={{ position: 'absolute', bottom: 44, left: 50, right: 50, opacity: kpOpacity }}>
        <div style={{ padding: '18px 28px', background: '#1a1008', border: '1px solid rgba(249,115,22,0.2)', borderRadius: 16, textAlign: 'center' }}>
          <span style={{ fontSize: 24, color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 }}>💡 {p.keyPoint}</span>
        </div>
      </div>

      {/* Progress */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, width: `${interpolate(frame, [0, 480], [0, 100], { extrapolateRight: 'clamp' })}%`, height: 3, background: 'linear-gradient(90deg, #f97316, #fbbf24)' }} />
    </AbsoluteFill>
  );
};
